import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '@env/environment';
import {
  CreateDesignHuddleProjectRequest,
  CreateDesignHuddleProjectResponse,
  DesignHuddleProjectStatus,
  DesignHuddleTemplate,
  GetTemplatesResponse,
  ExportDesignHuddleProjectRequest,
  ExportDesignHuddleProjectResponse,
  ExportJobStatusResponse
} from '@app/shared/models/design-huddle';
import { APIResponse } from '@app/shared/services/core/ICore.interface';

/**
 * Design Huddle Service
 * Handles all API communication with Design Huddle backend
 */
@Injectable({
  providedIn: 'root'
})
export class DesignHuddleService {

  constructor(private http: HttpClient) { }

  /**
   * Create a new Design Huddle project
   * @param request Project creation parameters
   * @returns Observable with project creation response
   */
  createProject(request: CreateDesignHuddleProjectRequest): Observable<APIResponse<CreateDesignHuddleProjectResponse>> {
    const headers = this.getAuthHeaders();
    return this.http.post<APIResponse<CreateDesignHuddleProjectResponse>>(
      `${environment.baseUrl}/api/DesignHuddle/projects/create`,
      request,
      { headers }
    );
  }

  /**
   * Get Design Huddle project details by project ID
   * @param projectId Design Huddle project ID
   * @returns Observable with project details
   */
  getProject(projectId: number): Observable<APIResponse<DesignHuddleProjectStatus>> {
    const headers = this.getAuthHeaders();
    return this.http.get<APIResponse<DesignHuddleProjectStatus>>(
      `${environment.baseUrl}/api/DesignHuddle/projects/${projectId}`,
      { headers }
    );
  }

  /**
   * Update Design Huddle project
   * @param projectId Project ID
   * @param updates Project updates
   * @returns Observable with updated project
   */
  updateProject(projectId: number, updates: Partial<CreateDesignHuddleProjectRequest>): Observable<APIResponse<any>> {
    const headers = this.getAuthHeaders();
    return this.http.put<APIResponse<any>>(
      `${environment.baseUrl}/api/DesignHuddle/projects/${projectId}`,
      updates,
      { headers }
    );
  }

  /**
   * Delete Design Huddle project
   * @param projectId Project ID
   * @returns Observable with deletion result
   */
  deleteProject(projectId: number): Observable<APIResponse<any>> {
    const headers = this.getAuthHeaders();
    return this.http.delete<APIResponse<any>>(
      `${environment.baseUrl}/api/DesignHuddle/projects/${projectId}`,
      { headers }
    );
  }

  /**
   * Get available Design Huddle templates
   * @param category Optional category filter
   * @param page Page number (default: 1)
   * @param perPage Items per page (default: 20)
   * @returns Observable with templates list
   */
  getTemplates(category?: string, page: number = 1, perPage: number = 20): Observable<APIResponse<GetTemplatesResponse>> {
    const headers = this.getAuthHeaders();
    let url = `${environment.baseUrl}/api/DesignHuddle/templates?page=${page}&perPage=${perPage}`;

    if (category) {
      url += `&category=${encodeURIComponent(category)}`;
    }

    return this.http.get<APIResponse<GetTemplatesResponse>>(url, { headers });
  }

  /**
   * Get specific template details
   * @param templateId Template ID
   * @returns Observable with template details
   */
  getTemplate(templateId: number): Observable<APIResponse<DesignHuddleTemplate>> {
    const headers = this.getAuthHeaders();
    return this.http.get<APIResponse<DesignHuddleTemplate>>(
      `${environment.baseUrl}/api/DesignHuddle/templates/${templateId}`,
      { headers }
    );
  }

  /**
   * Export Design Huddle project (generate print-ready files)
   * @param request Export request parameters
   * @returns Observable with export job response
   */
  exportProject(request: ExportDesignHuddleProjectRequest): Observable<APIResponse<ExportDesignHuddleProjectResponse>> {
    const headers = this.getAuthHeaders();
    return this.http.post<APIResponse<ExportDesignHuddleProjectResponse>>(
      `${environment.baseUrl}/api/DesignHuddle/projects/${request.projectId}/export`,
      {
        format: request.format || 'png',
        dpi: request.dpi || 300,
        colorMode: request.colorMode || 'cmyk',
        includeBleed: request.includeBleed !== undefined ? request.includeBleed : true
      },
      { headers }
    );
  }

  /**
   * Get export job status
   * @param exportJobId Export job ID
   * @returns Observable with export status
   */
  getExportStatus(exportJobId: number): Observable<APIResponse<ExportJobStatusResponse>> {
    const headers = this.getAuthHeaders();
    return this.http.get<APIResponse<ExportJobStatusResponse>>(
      `${environment.baseUrl}/api/DesignHuddle/export-jobs/${exportJobId}`,
      { headers }
    );
  }

  /**
   * Poll export job until completion
   * @param exportJobId Export job ID
   * @param maxAttempts Maximum polling attempts (default: 60)
   * @param intervalMs Polling interval in milliseconds (default: 2000)
   * @returns Observable with final export status
   */
  pollExportUntilComplete(
    exportJobId: number,
    maxAttempts: number = 60,
    intervalMs: number = 2000
  ): Observable<ExportJobStatusResponse> {
    return new Observable(observer => {
      let attempts = 0;

      const poll = () => {
        if (attempts >= maxAttempts) {
          observer.error(new Error(`Export job ${exportJobId} did not complete within ${maxAttempts} attempts`));
          return;
        }

        this.getExportStatus(exportJobId).subscribe({
          next: (response) => {
            if (response.StatusCode === 200 && response.Data?.ResponseData) {
              const status = response.Data.ResponseData;

              if (status.status === 'completed' || status.status === 'failed') {
                observer.next(status);
                observer.complete();
              } else {
                // Still processing, poll again
                attempts++;
                setTimeout(poll, intervalMs);
              }
            } else {
              observer.error(new Error('Invalid response from server'));
            }
          },
          error: (error) => {
            observer.error(error);
          }
        });
      };

      // Start polling
      poll();
    });
  }

  /**
   * Get customized product by Design Huddle project ID
   * @param projectId Design Huddle project ID
   * @returns Observable with customized product
   */
  getCustomizedProductByProjectId(projectId: number): Observable<APIResponse<any>> {
    const headers = this.getAuthHeaders();
    return this.http.get<APIResponse<any>>(
      `${environment.baseUrl}/api/DesignHuddle/customized-products/by-project/${projectId}`,
      { headers }
    );
  }

  /**
   * Get project data with position info from Design Huddle
   * @param projectId Design Huddle project ID
   * @returns Observable with raw project data including position info
   */
  getProjectData(projectId: string | number): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.get<any>(
      `${environment.baseUrl}/api/DesignHuddle/projects/${projectId}/data?include_position_info=true`,
      { headers }
    );
  }

  /**
   * Clone an existing Design Huddle project and return the cloned project's data.
   * Used to seed a non-locked color variant with the locked variant's design
   * instead of starting from a blank template.
   *
   * @param projectId   The source project ID to clone
   * @param guestToken  The current guest session's bearer token obtained via
   *                    DSHDLib.getGuestAccessToken().  When provided the backend
   *                    calls the non-partner DH endpoint with this token so the
   *                    cloned project is owned by the same guest user who owns
   *                    the source project.  Without it DH would assign ownership
   *                    to the partner account and the guest SDK session would be
   *                    denied access ("Access to this project is not authorized").
   * @returns Observable with clone response containing project_id of the new project
   */
  cloneProject(projectId: string, guestToken?: string): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.post<any>(
      `${environment.baseUrl}/api/DesignHuddle/projects/${projectId}/clone`,
      { guestToken: guestToken || null },
      { headers }
    );
  }

  checkProjectTitleExists(projectTitle: string, storeId: number): Observable<any> {
    const headers = this.getAuthHeaders();
    const url = `${environment.baseUrl}/api/DesignHuddle/CheckProjectTitleExists?projectTitle=${encodeURIComponent(projectTitle)}&storeId=${storeId}`;
    return this.http.get<any>(url, { headers });
  }

  /**
   * Get authentication headers for API requests
   * @returns HTTP headers with authorization token
   */
  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('userToken');
    let headers = new HttpHeaders();

    if (token) {
      headers = headers.append('Authorization', `Bearer ${token}`);
    }

    return headers;
  }
}
